import { useMemo, useState } from 'react';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { clearLocalDraft, saveLocalDraft } from '../hooks/useLocalDraft';
import { useSubscription } from '../hooks/useSubscription';
import { BusDispatch, BusEvent } from './EditorContext';
import { DocumentState } from './useDocumentState';

export type LocalBackupState = { active: boolean };

const isSyncFailedEvent = (e: BusEvent) => e.type === 'Editor' && e.event.type === 'SYNC_FAILED';
const isSyncSuccessEvent = (e: BusEvent) =>
  e.type === 'Editor' && e.event.type === 'SYNC_SUCCESSFUL';

const useLocalStorageBackup = (
  bus$: Subject<BusEvent>,
  dispatch: BusDispatch,
  id: string,
  document: DocumentState
): LocalBackupState => {
  const [state, setState] = useState(false);

  const failed$ = useMemo(() => bus$.pipe(filter(isSyncFailedEvent)), [bus$]);
  const success$ = useMemo(() => bus$.pipe(filter(isSyncSuccessEvent)), [bus$]);

  useSubscription(
    () =>
      failed$.subscribe((_) => {
        if (!state) {
          setState(true);
          dispatch({ type: 'Notify', event: 'ConnectivityIssue' });
        }
        // Save the thing
        document && saveLocalDraft(id, document);
        dispatch({ type: 'Editor', event: { type: 'SYNC_LOCAL' } });
      }),
    [failed$, dispatch, document, state, setState, id]
  );

  useSubscription(
    () =>
      success$.subscribe(() => {
        if (state) {
          setState(false);
          dispatch({ type: 'Editor', event: { type: 'INPUT' } });
          dispatch({ type: 'Notify', event: 'ReSynced' });
        }
        clearLocalDraft(id);
      }),
    [success$, state, setState, id, dispatch]
  );

  return { active: state };
};

export default useLocalStorageBackup;
